import React, { useMemo } from 'react';
import SafeHtml from '../../components/SafeHtml';
import { prepareCustomHtmlBlock } from '../../utils/customCss';

/**
 * The Custom HTML/CSS block: an author-supplied layout that takes over the body
 * of one slide.
 *
 * Unlike the embedded-HTML block this renders in the app's own DOM rather than
 * a sandboxed iframe, because its whole purpose is CSS that reaches the slide —
 * an iframe would seal the rules inside it and the "apply to the whole deck"
 * option could not exist at all. The markup is sanitized with the slide-layout
 * profile and the CSS is prefixed with this block's own selector, so a slide's
 * rules stay on that slide unless the author ticks deck scope.
 *
 * Deck-scoped CSS is emitted by LessonTemplate instead (one document-level
 * style element built from every deck-scoped block), so the rules stay live
 * while a different slide is on screen.
 */
const CustomHtmlRenderer = ({ block, isEditMode }) => {
  const { html, css, imageVarsCss, resetCss, baseCss, deckScoped } = useMemo(
    () => prepareCustomHtmlBlock(block),
    [block]
  );

  // Uploads stay scoped to this block even when the author's own rules are
  // global, so a deck-scoped block still gets its own pictures.
  //
  // The CSS reset ships here too, even though the app page already runs
  // Tailwind's preflight. Preflight's `h2 { font-size: inherit }` is a bare
  // element selector, so inside a canvas frame it loses to
  // `.slide-designer__item h2`, which sizes headings as a share of the slide.
  // The exported file has no such rule and used this block-scoped reset
  // instead — so the same unstyled heading came out slide-scaled in the editor
  // and inherited in the export. Emitting the identical sheet in both is what
  // makes the two agree by construction rather than by coincidence.
  const localCss = [resetCss, baseCss, imageVarsCss, deckScoped ? '' : css].filter(Boolean).join('\n\n');

  const isEmpty = !html.trim() && !css.trim() && !imageVarsCss;

  if (isEmpty) {
    return (
      <div className="slide-custom-html slide-custom-html--empty" data-block={block.id}>
        <div className="text-4xl" aria-hidden="true">🧩</div>
        <h3 className="text-base font-semibold text-gray-700 mt-2">Custom HTML / CSS</h3>
        <p className="text-sm text-gray-500 mt-1">
          {isEditMode
            ? 'Click the edit button on this block to write the markup and styles for this slide.'
            : 'No custom layout has been written for this slide yet.'}
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Slide-scoped rules ride with the block. Deck-scoped ones are hoisted
          to the document so they survive navigating away from this slide. */}
      {localCss && <style>{localCss}</style>}
      <SafeHtml
        profile="slideLayout"
        className="slide-custom-html"
        data-block={block.id}
        data-css-scope={deckScoped ? 'deck' : 'slide'}
        html={html}
      />
      {isEditMode && deckScoped && (
        <p className="slide-custom-html__scope-note">
          This block's CSS is applied to every slide in the deck.
        </p>
      )}
    </>
  );
};

export default CustomHtmlRenderer;
